import React, { Component, Suspense, lazy } from 'react';
import { debounce } from 'lodash';
import { Row, Col, Card, Button } from 'antd';
import * as echarts from 'echarts';
import { fetchStoreList, paginateStoreData } from '@/redux/dashboard/storeList/storeSlice';
import { fetchRoomType } from '@/redux/dashboard/roomType/roomTypeSlice';
import { fetchStaffList } from '@/redux/dashboard/staffList/StaffSlice';
import { useSelector, useDispatch } from 'react-redux';

import Header from './modal/Header';
const GroupIntroduction = lazy(() => import('./modal/GroupIntroduction'));
const SwiperPage = lazy(() => import('./modal/Swiper'));
const HomesSwiperPage = lazy(() => import('./modal/HomesSwiper'));
import HomeVideo from './modal/HomeVideo';
const Heatmap = lazy(() => import('./modal/Heatmap'));
const Realtime = lazy(() => import('./modal/Realtime'));
const StaffList = lazy(() => import('./modal/StaffList'));

import { SelectC } from '@/i2antdV5'
import StoreModal from './share/StoreModal'

import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import './Page.less';

const areaOption = [
  { id: '1', value: '白云区' },
  { id: '2', value: '天河区' },
  { id: '3', value: '越秀区' },
  { id: '4', value: '海珠区' }
]
const KPI_DATA = [
  { label: '联盟门店', value: 12, unit: '家', color: '#00d4ff' },
  { label: '总房源数', value: 368, unit: '间', color: '#00e396' },
  { label: '在岗员工', value: 256, unit: '人', color: '#feb019' },
  { label: '服务中订单', value: 89, unit: '单', color: '#ff4560' },
  { label: '当月入住率', value: 87.5, unit: '%', color: '#775dd0' },
  { label: '客户满意度', value: 98.2, unit: '%', color: '#00d4ff' },
];

/** 科幻面板容器 */
const SciFiPanel = ({ title, children, className = '', style = {} }) => (
  <div className={`sci-fi-panel ${className}`} style={style}>
    <div className="panel-corner tl" />
    <div className="panel-corner tr" />
    <div className="panel-corner bl" />
    <div className="panel-corner br" />
    {title && (
      <div className="panel-header">
        <span className="panel-header-icon" />
        <span className="panel-header-title">{title}</span>
        <span className="panel-header-line" />
      </div>
    )}
    <div className="panel-body">{children}</div>
  </div>
);

/** KPI 数字动画 */
class AnimatedNumber extends Component {
  state = { display: 0 };
  rafId = null;

  componentDidMount() { this.animate(0, this.props.value); }
  componentDidUpdate(prev) {
    if (prev.value !== this.props.value) this.animate(this.state.display, this.props.value);
  }
  componentWillUnmount() { if (this.rafId) cancelAnimationFrame(this.rafId); }

  animate(from, to) {
    const duration = 1500;
    const start = performance.now();
    const isFloat = !Number.isInteger(to);
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      const current = from + (to - from) * ease;
      this.setState({ display: isFloat ? current.toFixed(1) : Math.round(current) });
      if (progress < 1) this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  render() { return <span>{this.state.display}</span>; }
}

/**
 * 可视化大屏主页 - 科技公司大屏风格
 */
class VisualDashboardClass extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedDistrict: '白云区',
      selectedCenter: '广州市金宝宝月子中心'
    };
    this.serviceChartRef = React.createRef();
    this.serviceChart = null;
    this.handleStoreChange = debounce(this.handleStoreChangeCore, 200);
  }

  componentDidMount() {
    const { dispatch } = this.props;
    dispatch(fetchStoreList({ serviceType: 'default', dataType: 'swiper,storeOption', query: {} }));
    dispatch(fetchRoomType({ serviceType: 'default', dataType: 'groupByData,url720,videoList', query: { groupByKey: 'room_type_name' } }));
    dispatch(fetchStaffList({ serviceType: 'default', dataType: 'default' }));
    setTimeout(this.initCharts, 300);
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    if (this.serviceChart) this.serviceChart.dispose();
    window.removeEventListener('resize', this.handleResize);
    if (this.handleStoreChange && this.handleStoreChange.cancel) this.handleStoreChange.cancel();
  }

  handleResize = () => {
    if (this.serviceChart) this.serviceChart.resize();
  };

  initCharts = () => {
    if (!this.serviceChartRef.current) {
      setTimeout(this.initCharts, 100);
      return;
    }
    try {
      this.serviceChart = echarts.init(this.serviceChartRef.current);
      this.serviceChart.setOption({
        backgroundColor: 'transparent',
        tooltip: { trigger: 'item', backgroundColor: 'rgba(10, 22, 40, 0.95)', borderColor: 'rgba(0, 212, 255, 0.4)', textStyle: { color: '#c8e6ff' } },
        legend: { bottom: 0, textStyle: { color: '#7eb8da', fontSize: 11 }, itemWidth: 10, itemHeight: 10, itemGap: 12 },
        series: [{
          name: '服务阶段',
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['50%', '42%'],
          avoidLabelOverlap: false,
          itemStyle: { borderRadius: 4, borderColor: '#0a1628', borderWidth: 2 },
          label: {
            show: true,
            color: '#aee2ff',
            fontSize: 11,
            formatter: '{b}\n{d}%'
          },
          emphasis: {
            label: { show: true, fontSize: 13, fontWeight: 'bold' },
            itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0, 212, 255, 0.5)' }
          },
          data: [
            { value: 1048, name: '预约中', itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#00d4ff' }, { offset: 1, color: '#0088cc' }]) } },
            { value: 735, name: '上门服务', itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#00e396' }, { offset: 1, color: '#00a86b' }]) } },
            { value: 580, name: '服务中', itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#feb019' }, { offset: 1, color: '#cc8a00' }]) } },
            { value: 484, name: '回访', itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#ff4560' }, { offset: 1, color: '#cc2233' }]) } },
            { value: 300, name: '已完成', itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#775dd0' }, { offset: 1, color: '#5533aa' }]) } }
          ]
        }]
      });
    } catch (e) {
      setTimeout(this.initCharts, 200);
    }
  };

  handleStoreChangeCore = (value) => {
    const { dispatch } = this.props;
    const storeId = value.storeId || value.id;
    if (storeId) {
      dispatch(paginateStoreData({ serviceType: 'default', dataType: 'pictureList', query: { store_id: storeId } }));
    }
  }

  render() {
    const { storeObj, roomTypeObj, staffListObj } = this.props;
    const { default_storeOption: storeOption, default_swiper: swiperData, query } = storeObj;
    const { data: roomTypeData, default_videoList, default_groupByData: roomTypeGroupByData } = roomTypeObj;
    const storeModalTitle = (storeOption || []).find(item => item.id === this.props.storeObj.query.default.store_id)?.value || '查看详情';

    return (
      <div className="visual-screen">
        <Header />

        {/* KPI 数据条 */}
        <div className="kpi-bar">
          {KPI_DATA.map((item, idx) => (
            <div className="kpi-card" key={idx}>
              <div className="kpi-value" style={{ color: item.color }}>
                <AnimatedNumber value={item.value} />
                <span className="kpi-unit">{item.unit}</span>
              </div>
              <div className="kpi-label">{item.label}</div>
              <div className="kpi-glow" style={{ background: item.color }} />
            </div>
          ))}
        </div>

        <div className="screen-body">
          {/* 背景地图层 */}
          <div className="map-bg">
            <Suspense fallback={null}>
              <Heatmap />
            </Suspense>
          </div>

          <Row gutter={12} className="main-row">
            {/* 左侧栏 */}
            <Col span={7} className="column column-left">
              {/* 月子会所机构展示区 */}
              <SciFiPanel title="联盟月子会所机构" className="panel-store">
                <section className="i2-app-toolbar compact-toolbar">
                  <div className='toolbar-mode'>
                    <SelectC label='' searchKey='areaId' modalProps={{ style: { minWidth: '80px' } }} query={query} options={areaOption} action={this.handleStoreChange} />
                    <SelectC label='' searchKey='storeId' modalProps={{ style: { minWidth: '170px' } }} query={query} options={storeOption} action={this.handleStoreChange} />
                  </div>
                  <div className='toolbar-right-btn'>
                    <StoreModal title={storeModalTitle} baseProps={{ fields: storeObj }}>
                      <Button type="primary" size="small">查看详情</Button>
                    </StoreModal>
                  </div>
                </section>
                <Suspense fallback={null}>
                  <SwiperPage imageList={swiperData} data={storeObj} />
                </Suspense>
              </SciFiPanel>

              {/* 月子中心房源信息 */}
              <SciFiPanel title="月子中心房源信息" className="panel-rooms">
                <Suspense fallback={null}>
                  <HomesSwiperPage imageList={roomTypeGroupByData} data={roomTypeData} />
                </Suspense>
              </SciFiPanel>

              {/* 视频/VR 展示 */}
              <SciFiPanel title="720° VR 选房" className="panel-vr">
                <Suspense fallback={null}>
                  <HomeVideo videoList={default_videoList} />
                </Suspense>
              </SciFiPanel>
            </Col>

            {/* 中间主区域 */}
            <Col span={10} className="column column-center">
              <Suspense fallback={null}>
                <GroupIntroduction />
              </Suspense>
            </Col>

            {/* 右侧栏 */}
            <Col span={7} className="column column-right">
              <SciFiPanel title="在岗育婴师 / 月嫂" className="panel-staff-on">
                <Suspense fallback={null}>
                  <StaffList obj={staffListObj} />
                </Suspense>
              </SciFiPanel>

              <SciFiPanel title="在培训育婴师 / 月嫂" className="panel-staff-train">
                <Suspense fallback={null}>
                  <StaffList obj={staffListObj} />
                </Suspense>
              </SciFiPanel>

              <SciFiPanel title="服务进度分析" className="panel-service-chart">
                <div ref={this.serviceChartRef} className="chart-box" />
              </SciFiPanel>

              <SciFiPanel title="服务监控实况" className="panel-monitor">
                <Suspense fallback={null}>
                  <Realtime />
                </Suspense>
              </SciFiPanel>
            </Col>
          </Row>
        </div>
      </div>
    );
  }
}

const VisualDashboardWrapper = () => {
  const dispatch = useDispatch();
  const props = {
    dispatch,
    testObj: useSelector(state => state.test || {}),
    storeObj: useSelector(state => state.dashboard.storeList || {}),
    roomTypeObj: useSelector(state => state.dashboard.roomType || {}),
    staffListObj: useSelector(state => state.dashboard.staffList || {}),
    app: useSelector(state => state.app || {})
  }
  return <VisualDashboardClass {...props} />;
};

export default VisualDashboardWrapper;
